import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import {LinksExchangeShowCreateResultParams} from '@yoroi/links'
import {Exchange, Links} from '@yoroi/types'
import * as React from 'react'

import {LargeYSpace, SmallXSpace} from '../common/space'
import {
  PartnerInfo,
  filterPartnerInfo,
  usePartnerInfo,
} from '../common/partner-info'
import {isNumber} from '../common/isNumber'
import {orderShowCreateResult} from 'api/orderShowCreateResult'

export const ExchangeOrderResultLink = () => {
  const [scheme, setScheme] = React.useState<
    Links.YoroiUriConfig['scheme'] | ''
  >('')
  const [status, setStatus] = React.useState<
    LinksExchangeShowCreateResultParams['status'] | ''
  >('')
  const [orderType, setOrderType] = React.useState<Exchange.OrderType | ''>('')
  const [provider, setProvider] = React.useState('')
  const [coin, setCoin] = React.useState('')
  const [coinAmount, setCoinAmount] = React.useState('')
  const [fiat, setFiat] = React.useState('')
  const [fiatAmount, setFiatAmount] = React.useState('')
  const [exchangeLink, setExchangeLink] = React.useState('')

  const [schemaError, setSchemeError] = React.useState(false)
  const [providerError, setProviderError] = React.useState(false)
  const [statusError, setStatusError] = React.useState(false)
  const [coinError, setCoinError] = React.useState(false)
  const [coinAmountError, setCoinAmountError] = React.useState(false)
  const [fiatError, setFiatError] = React.useState(false)
  const [fiatAmountError, setFiatAmountError] = React.useState(false)
  const [orderTypeError, setOrderTypeError] = React.useState(false)
  const [linkError, setLinkError] = React.useState(false)

  const partnerInfo = usePartnerInfo()

  const noSchema = scheme === ''
  const noProvider = provider === ''
  const noOrderType = orderType === ''

  const disabled = noSchema || noProvider || noOrderType

  const onGenerateLink = React.useCallback(() => {
    if (noSchema) setSchemeError(true)
    if (noProvider) setProviderError(true)
    if (noOrderType) setOrderTypeError(true)
    if (disabled) return

    const filteredPartnerInfo = filterPartnerInfo(partnerInfo)

    const {link, error} = orderShowCreateResult({
      provider,
      scheme,
      coin,
      coinAmount: Number(coinAmount),
      fiatAmount: Number(fiatAmount),
      fiat,
      status,
      orderType,
      partnerInfo: filteredPartnerInfo,
    })

    if (link && !error) {
      if (linkError) setLinkError(false)
      setExchangeLink(link)
      return
    }

    setLinkError(true)
  }, [
    noSchema,
    noProvider,
    noOrderType,
    disabled,
    partnerInfo,
    coinAmount,
    provider,
    scheme,
    coin,
    fiatAmount,
    fiat,
    status,
    orderType,
    linkError,
  ])

  return (
    <Box sx={{width: '100%', display: 'flex', flexDirection: 'column', p: 1}}>
      <LargeYSpace />

      <Box sx={{display: 'flex'}}>
        <FormControl>
          <InputLabel id="scheme-label">Schema</InputLabel>

          <Select
            sx={{width: '200px'}}
            labelId="scheme-label"
            id="scheme"
            value={scheme}
            label="Schema"
            variant="outlined"
            onChange={(event) => {
              setScheme(event.target.value as Links.YoroiUriConfig['scheme'])
              setSchemeError(false)
            }}
            error={schemaError}
          >
            <MenuItem value="yoroi">yoroi</MenuItem>

            <MenuItem value="https">https</MenuItem>
          </Select>
        </FormControl>

        <SmallXSpace />

        <TextField
          onChange={(event) => {
            setProvider(event.target.value)
            setProviderError(false)
          }}
          sx={{width: '200px'}}
          id="provider"
          label="Provider"
          variant="outlined"
          value={provider}
          error={providerError}
        />
      </Box>

      <LargeYSpace />

      <Box sx={{display: 'flex'}}>
        <FormControl>
          <InputLabel id="status-label">Status</InputLabel>

          <Select
            sx={{width: '200px'}}
            labelId="status-label"
            id="status"
            value={status}
            label="Status (optional)"
            variant="outlined"
            onChange={(event) => {
              setStatus(
                event.target
                  .value as LinksExchangeShowCreateResultParams['status'],
              )
              setStatusError(false)
            }}
            error={statusError}
          >
            <MenuItem value="success">Success</MenuItem>

            <MenuItem value="pending">Pending</MenuItem>

            <MenuItem value="error">Error</MenuItem>
          </Select>
        </FormControl>

        <SmallXSpace />

        <FormControl>
          <InputLabel id="order-type-label">Order Type</InputLabel>

          <Select
            sx={{width: '200px'}}
            labelId="order-type-label"
            id="order-type"
            value={orderType}
            label="Order Type"
            variant="outlined"
            onChange={(event) => {
              setOrderType(event.target.value as Exchange.OrderType)
              setOrderTypeError(false)
            }}
            error={orderTypeError}
          >
            <MenuItem value="buy">Buy</MenuItem>

            <MenuItem value="sell">Sell</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <LargeYSpace />

      <Box sx={{display: 'flex'}}>
        <TextField
          onChange={(event) => {
            setCoin(event.target.value)
            setCoinError(false)
          }}
          id="coin"
          label="Coin (optional)"
          variant="outlined"
          value={coin}
          error={coinError}
        />

        <SmallXSpace />

        <TextField
          onChange={(event) => {
            setCoinAmount(event.target.value)
            setCoinAmountError(false)
          }}
          id="coin-amount"
          label="Coin Amount (optional)"
          variant="outlined"
          value={coinAmount}
          error={coinAmountError}
          helperText={
            coinAmountError && !isNumber(coinAmount) && 'Not a number'
          }
        />
      </Box>

      <LargeYSpace />

      <Box sx={{display: 'flex'}}>
        <TextField
          onChange={(event) => {
            setFiat(event.target.value)
            setFiatError(false)
          }}
          id="fiat"
          label="Fiat (optional)"
          variant="outlined"
          value={fiat}
          error={fiatError}
        />

        <SmallXSpace />

        <TextField
          onChange={(event) => {
            setFiatAmount(event.target.value)
            setFiatAmountError(false)
          }}
          id="fiat-amount"
          label="Fiat Amount (optional)"
          variant="outlined"
          value={fiatAmount}
          error={fiatAmountError}
          helperText={
            fiatAmountError && !isNumber(fiatAmount) && 'Not a number'
          }
        />
      </Box>

      <LargeYSpace />

      <PartnerInfo {...partnerInfo} />

      <LargeYSpace />

      <Button
        onClick={onGenerateLink}
        sx={{width: '150px'}}
        variant="contained"
        disabled={disabled}
      >
        Generate Link
      </Button>

      <LargeYSpace />

      <Divider />

      <Typography sx={{fontSize: 18, fontWeight: 600}}>Link:</Typography>

      {linkError ? (
        <Typography sx={{color: 'red'}}>Error generating link</Typography>
      ) : (
        <Typography sx={{wordBreak: 'break-word'}}>{exchangeLink}</Typography>
      )}

      <LargeYSpace />

      <Button
        onClick={() => {
          navigator.clipboard.writeText(exchangeLink)
        }}
        sx={{width: '150px'}}
        variant="contained"
        disabled={exchangeLink === ''}
      >
        Copy
      </Button>

      <LargeYSpace />

      <Button
        onClick={() => {
          window.location.replace(exchangeLink)
        }}
        sx={{width: '150px'}}
        variant="contained"
        disabled={exchangeLink === ''}
      >
        Open
      </Button>
    </Box>
  )
}
